/**
 * Safe Coercion Helpers
 * Provide type-safe coercion with fallback defaults for API response handling.
 * @module api/safeHelpers
 */

/**
 * Coerce value to string with fallback
 * @param {*} value - Value to coerce
 * @param {string} [defaultValue=''] - Default fallback
 * @returns {string}
 */
export function safeString(value, defaultValue = "") {
  if (value === null || value === undefined) return defaultValue
  return String(value)
}

/**
 * Coerce value to array with fallback
 * @param {*} value - Value to coerce
 * @param {Array} [defaultValue=[]] - Default fallback
 * @returns {Array}
 */
export function safeArray(value, defaultValue = []) {
  if (value === null || value === undefined) return defaultValue
  return Array.isArray(value) ? value : defaultValue
}

/**
 * Coerce value to object with fallback
 * @param {*} value - Value to coerce
 * @param {Object} [defaultValue={}] - Default fallback
 * @returns {Object}
 */
export function safeObject(value, defaultValue = {}) {
  if (value === null || value === undefined) return defaultValue
  return typeof value === "object" && !Array.isArray(value)
    ? value
    : defaultValue
}

/**
 * Coerce value to number with fallback
 * @param {*} value - Value to coerce
 * @param {number} [defaultValue=0] - Default fallback
 * @returns {number}
 */
export function safeNumber(value, defaultValue = 0) {
  if (value === null || value === undefined) return defaultValue
  const num = Number(value)
  return isNaN(num) ? defaultValue : num
}

/**
 * Coerce value to boolean with fallback
 * @param {*} value - Value to coerce
 * @param {boolean} [defaultValue=false] - Default fallback
 * @returns {boolean}
 */
export function safeBoolean(value, defaultValue = false) {
  if (value === null || value === undefined) return defaultValue
  if (typeof value === "boolean") return value
  if (typeof value === "number") return value !== 0
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase()
    if (["true", "1", "yes", "y"].includes(normalized)) return true
    if (["false", "0", "no", "n", ""].includes(normalized)) return false
  }
  return defaultValue
}
